/**
 * VANYA collection — grid column density + filter drawer helpers.
 */
(function () {
  const STORAGE_DESKTOP = 'vanya-grid-columns-desktop';
  const STORAGE_MOBILE = 'vanya-grid-columns-mobile';
  const DEFAULT_DESKTOP = '4';
  const DEFAULT_MOBILE = '2';

  function getGrids(root) {
    return root.querySelectorAll('.product-grid');
  }

  function applyDesktopColumns(grids, cols) {
    const view = cols === 'list' ? 'list' : `cols-${cols}`;
    grids.forEach((grid) => grid.setAttribute('product-grid-view', view));
    sessionStorage.setItem(STORAGE_DESKTOP, cols);
  }

  function applyMobileColumns(grids, cols) {
    grids.forEach((grid) => grid.style.setProperty('--vanya-mobile-cols', cols));
    sessionStorage.setItem(STORAGE_MOBILE, cols);
  }

  function restoreLayout(root) {
    const grids = getGrids(root);
    if (!grids.length) return;

    const desktopCols = sessionStorage.getItem(STORAGE_DESKTOP) || DEFAULT_DESKTOP;
    const mobileCols = sessionStorage.getItem(STORAGE_MOBILE) || DEFAULT_MOBILE;

    applyDesktopColumns(grids, desktopCols);
    applyMobileColumns(grids, mobileCols);

    root.querySelectorAll('input[name="vanya-grid"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === desktopCols;
      }
    });

    root.querySelectorAll('input[name="vanya-grid-mobile"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === mobileCols;
      }
    });
  }

  function parseRangeAmount(value) {
    if (value == null || value === '') return 0;
    const normalized = String(value).replace(/[^0-9.-]/g, '');
    const numeric = Number.parseFloat(normalized);
    return Number.isNaN(numeric) ? 0 : numeric;
  }

  function formatPriceLabel(amount, moneyFormat) {
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) return '0.00';

    const fixed = numeric.toFixed(2);
    const withCommas = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (moneyFormat && moneyFormat.includes('{{amount}}')) {
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, withCommas);
    }

    return withCommas;
  }

  function findPriceInputs(priceFacet) {
    const minInput =
      priceFacet.querySelector('input[ref="minInput"]') ||
      priceFacet.querySelector('input[name*="price.gte"]');
    const maxInput =
      priceFacet.querySelector('input[ref="maxInput"]') ||
      priceFacet.querySelector('input[name*="price.lte"]');

    return {
      minInput: minInput instanceof HTMLInputElement ? minInput : null,
      maxInput: maxInput instanceof HTMLInputElement ? maxInput : null,
    };
  }

  function bindPriceRange(rangeEl) {
    if (rangeEl.dataset.vanyaPriceInit === 'true') return;

    const priceFacet = rangeEl.closest('price-facet-component');
    if (!(priceFacet instanceof HTMLElement)) return;

    const { minInput, maxInput } = findPriceInputs(priceFacet);
    const minSlider = rangeEl.querySelector('.vanya-price-range__input--min');
    const maxSlider = rangeEl.querySelector('.vanya-price-range__input--max');
    const progress = rangeEl.querySelector('.vanya-price-range__progress');
    const label = rangeEl.querySelector('.vanya-price-range__text');
    const filterBtn = rangeEl.querySelector('.vanya-price-range__filter-btn');
    let rangeMax = parseRangeAmount(rangeEl.dataset.rangeMax);
    const moneyFormat = rangeEl.dataset.moneyFormat || '{{amount}}';

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    if (!rangeMax) {
      rangeMax = parseRangeAmount(maxSlider.max);
    }

    if (!minInput || !maxInput || !progress || !label || !rangeMax) {
      return;
    }

    minSlider.max = String(rangeMax);
    maxSlider.max = String(rangeMax);

    const updateProgress = () => {
      const minVal = Math.min(Number(minSlider.value), Number(maxSlider.value));
      const maxVal = Math.max(Number(minSlider.value), Number(maxSlider.value));
      const minPercent = (minVal / rangeMax) * 100;
      const maxPercent = (maxVal / rangeMax) * 100;

      progress.style.left = `${minPercent}%`;
      progress.style.width = `${Math.max(maxPercent - minPercent, 0)}%`;
    };

    const syncInputs = () => {
      let minVal = Number(minSlider.value);
      let maxVal = Number(maxSlider.value);

      if (minVal > maxVal) {
        if (document.activeElement === minSlider) {
          maxVal = minVal;
          maxSlider.value = String(maxVal);
        } else {
          minVal = maxVal;
          minSlider.value = String(minVal);
        }
      }

      minInput.value = minVal > 0 ? String(minVal) : '';
      maxInput.value = maxVal < rangeMax ? String(maxVal) : '';

      label.innerHTML = `Price: <strong>${formatPriceLabel(minVal, moneyFormat)}</strong> — <strong>${formatPriceLabel(maxVal, moneyFormat)}</strong>`;
      updateProgress();
    };

    const applyPriceFilter = () => {
      syncInputs();
      if (typeof priceFacet.updatePriceFilterAndResults === 'function') {
        priceFacet.updatePriceFilterAndResults();
      } else {
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    minSlider.addEventListener('input', syncInputs);
    maxSlider.addEventListener('input', syncInputs);

    if (filterBtn instanceof HTMLButtonElement) {
      filterBtn.addEventListener('click', (event) => {
        event.preventDefault();
        applyPriceFilter();
      });
    }

    syncInputs();
  }

  function bindPriceRanges(root) {
    root.querySelectorAll('.vanya-facets-drawer .vanya-price-range').forEach((rangeEl) => {
      if (rangeEl instanceof HTMLElement) {
        bindPriceRange(rangeEl);
        rangeEl.dataset.vanyaPriceInit = 'true';
      }
    });
  }

  function prepareVanyaDrawer(root) {
    const drawer = root.querySelector('.vanya-facets-drawer');
    if (!(drawer instanceof HTMLElement)) return;

    drawer.querySelectorAll('accordion-custom details').forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        details.open = true;
      }
    });

    drawer.querySelectorAll('floating-panel-component').forEach((panel) => {
      if (panel instanceof HTMLElement) {
        panel.style.top = 'auto';
        panel.style.left = 'auto';
        panel.style.position = 'static';
      }
    });
  }

  function bindControls(root) {
    if (root.dataset.vanyaControlsBound === 'true') return;
    root.dataset.vanyaControlsBound = 'true';

    root.querySelectorAll('input[name="vanya-grid"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!(input instanceof HTMLInputElement) || !input.checked) return;
        applyDesktopColumns(getGrids(root), input.value);
      });
    });

    root.querySelectorAll('input[name="vanya-grid-mobile"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!(input instanceof HTMLInputElement) || !input.checked) return;
        applyMobileColumns(getGrids(root), input.value);
      });
    });
  }

  function watchVanyaDrawer(root) {
    const drawer = root.querySelector('.vanya-facets-drawer');
    if (!(drawer instanceof HTMLDialogElement)) return;
    if (drawer.dataset.vanyaDrawerWatch === 'true') return;

    drawer.dataset.vanyaDrawerWatch = 'true';
    const observer = new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) return;
      prepareVanyaDrawer(root);
      bindPriceRanges(root);
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  function refreshAfterFilterUpdate(root) {
    prepareVanyaDrawer(root);
    root
      .querySelectorAll('.vanya-facets-drawer .vanya-price-range[data-vanya-price-init]')
      .forEach((rangeEl) => {
        if (rangeEl instanceof HTMLElement) {
          delete rangeEl.dataset.vanyaPriceInit;
        }
      });
    bindPriceRanges(root);
  }

  function init(root) {
    if (!root) return;
    restoreLayout(root);
    bindControls(root);
    prepareVanyaDrawer(root);
    bindPriceRanges(root);
    watchVanyaDrawer(root);
  }

  function initAll() {
    document.querySelectorAll('.vanya-collection').forEach(init);
  }

  function onDrawerToggle(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const drawer = target.closest('.vanya-facets-drawer');
    if (!drawer) return;

    const collection = drawer.closest('.vanya-collection');
    if (collection instanceof HTMLElement) {
      window.requestAnimationFrame(() => {
        prepareVanyaDrawer(collection);
        bindPriceRanges(collection);
      });
    }
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('filter:update', () => {
    document.querySelectorAll('.vanya-collection').forEach(refreshAfterFilterUpdate);
  });
  document.addEventListener('toggle', onDrawerToggle, true);
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement) {
      const section =
        target.querySelector('.vanya-collection') ||
        (target.classList.contains('vanya-collection') ? target : null);
      if (section instanceof HTMLElement) init(section);
    }
  });

  window.matchMedia('(min-width: 750px)').addEventListener('change', initAll);
})();
